import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useAuth from './useAuth';
import { useUserProfile } from './useUserProfile';
import { trainingsDB } from '../lib/db/trainings';
import { schoolsDB } from '../lib/db/schools';
import { trackEvent, EVENTS } from '../lib/analytics';
import { subDays } from 'date-fns';

const STORAGE_KEY = 'recommendations';
const COOLDOWN_HOURS = 24;

// Generate a unique internal ID
const generateInternalId = () => {
  return `rec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Load recommendations from localStorage
const loadStoredRecommendations = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error loading recommendations from localStorage:', error);
    return null;
  }
};

// Save recommendations to localStorage
const saveStoredRecommendations = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving recommendations to localStorage:', error);
  }
};

// Check if 24 hours have passed since last update
const shouldCreateNewRecommendations = (lastUpdated) => {
  if (!lastUpdated) return true;
  const lastUpdatedTime = new Date(lastUpdated).getTime();
  const now = new Date().getTime();
  const hoursSinceUpdate = (now - lastUpdatedTime) / (1000 * 60 * 60);
  return hoursSinceUpdate >= COOLDOWN_HOURS;
};

const useRecommendations = () => {
  const { currentUser } = useAuth();
  const { userProfile } = useUserProfile();
  const { i18n, t } = useTranslation();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateRecommendations = async () => {
      if (!currentUser || !userProfile?.profile?.schoolId) {
        setLoading(false);
        return;
      }

      try {
        // Load stored recommendations
        const stored = loadStoredRecommendations();
        
        // Check if we should create new recommendations
        const shouldCreate = shouldCreateNewRecommendations(stored?.lastUpdated);
        
        if (stored?.recommendations && !shouldCreate) {
          // Use stored recommendations if they exist and cooldown hasn't passed
          const activeRecommendations = stored.recommendations.filter(
            (rec) => rec.status !== 'dismissed'
          );
          // If all recommendations were dismissed, regenerate immediately
          // rather than showing nothing until the 24h cooldown expires
          if (activeRecommendations.length > 0) {
            setRecommendations(activeRecommendations);
            setLoading(false);
            return;
          }
        }

        // Fetch trainings from the past month
        const monthStart = subDays(new Date(), 30);
        const trainings = await trainingsDB.getTrainings(currentUser.uid, {
          startDate: monthStart,
          endDate: new Date(),
        });

        // Count drills practiced
        const drillCounts = new Map();
        const focusCounts = new Map();

        trainings.forEach((training) => {
          if (training.drills && Array.isArray(training.drills)) {
            training.drills.forEach((drill) => {
              const drillId = drill.drillId;
              if (drillId) {
                const count = drillCounts.get(drillId) || 0;
                drillCounts.set(drillId, count + 1);
              }

              // Count focuses
              if (drill.focus && Array.isArray(drill.focus)) {
                drill.focus.forEach((focusId) => {
                  const count = focusCounts.get(focusId) || 0;
                  focusCounts.set(focusId, count + 1);
                });
              }
            });
          }
        });

        // Get all available drills for the school
        const allDrills = await schoolsDB.getAllDrills(userProfile.profile.schoolId);
        
        // Get belts data to filter drills by belt level
        const belts = await schoolsDB.getBelts(userProfile.profile.schoolId);
        const userBeltId = userProfile.profile.currentBelt;
        
        // Get user's belt level
        const getUserBeltLevel = () => {
          return belts.find(b => b.id === userBeltId)?.level ?? -1;
        };
        
        // Check if drill is available for user's belt level
        // A drill is available if ANY belt containing it has level <= user's belt level
        const isAvailableForBelt = (drill) => {
          const userLevel = getUserBeltLevel();
          return belts.some(belt => 
            belt.drillIds.includes(drill.id) && belt.level <= userLevel
          );
        };
        
        // Filter drills to only include those available for the user's belt level
        const availableDrills = allDrills.filter(isAvailableForBelt);
        
        // Get school data for focuses
        const school = await schoolsDB.getSchool(userProfile.profile.schoolId);
        const schoolFocuses = school?.focus || [];

        // Find drills that were practiced (to prioritize those over never-practiced)
        // Sort by practice count (least practiced first), then by name for consistency
        const drillPracticeStats = availableDrills.map((drill) => ({
          drill,
          practiceCount: drillCounts.get(drill.id) || 0,
        }));

        // Sort by practice count ascending, then by name
        drillPracticeStats.sort((a, b) => {
          if (a.practiceCount !== b.practiceCount) {
            return a.practiceCount - b.practiceCount;
          }
          const nameA = a.drill.nameHe || a.drill.nameEn || a.drill.name || '';
          const nameB = b.drill.nameHe || b.drill.nameEn || b.drill.name || '';
          return nameA.localeCompare(nameB);
        });

        // Get the 3 least practiced drills
        const leastPracticedDrills = drillPracticeStats.slice(0, 3).map((stat) => stat.drill);

        // Find the least practiced focus
        const focusStats = schoolFocuses.map((focus) => ({
          focus,
          practiceCount: focusCounts.get(focus.id) || 0,
        }));

        // Sort focuses by practice count
        focusStats.sort((a, b) => {
          if (a.practiceCount !== b.practiceCount) {
            return a.practiceCount - b.practiceCount;
          }
          const nameA = a.focus.nameHe || a.focus.nameEn || '';
          const nameB = b.focus.nameHe || b.focus.nameEn || '';
          return nameA.localeCompare(nameB);
        });

        // Get the 3 least practiced focuses
        const leastPracticedFocuses = focusStats.slice(0, 3).map((stat) => stat.focus);

        // Create recommendations
        const newRecommendations = leastPracticedDrills.map((drill, index) => {
          const focus = leastPracticedFocuses[index] || leastPracticedFocuses[leastPracticedFocuses.length - 1] || null;
          const focusName = focus
            ? (i18n.language === 'he' 
                ? focus.nameHe 
                : focus.nameEn) || focus.name
            : t('recommendations.your_practice');
          
          const description = t('recommendations.focus_description', { focusName });
          
          return {
            id: drill.id,
            name: i18n.language === 'he' 
              ? (drill.nameHe || drill.nameEn || drill.name)
              : (drill.nameEn || drill.name),
            description,
            focusId: focus?.id || null,
            'internal-id': generateInternalId(),
            'created-at': new Date().toISOString(),
            status: 'active',
          };
        });

        // Save fresh recommendations — dismissed status resets each day
        saveStoredRecommendations({
          recommendations: newRecommendations,
          lastUpdated: new Date().toISOString(),
        });

        setRecommendations(newRecommendations);
      } catch (error) {
        console.error('Error generating recommendations:', error);
        // Fallback to stored recommendations if available
        const stored = loadStoredRecommendations();
        if (stored?.recommendations) {
          const activeRecommendations = stored.recommendations.filter(
            (rec) => rec.status !== 'dismissed'
          );
          setRecommendations(activeRecommendations);
        } else {
          setRecommendations([]);
        }
      } finally {
        setLoading(false);
      }
    };

    generateRecommendations();
  }, [currentUser, userProfile, i18n.language]);

  const dismissRecommendation = (id) => {
    // Fire analytics event
    trackEvent(EVENTS.RECOMMENDATION_DISMISSED, {
      recommendationId: id,
    });

    // Update recommendation status in localStorage
    const stored = loadStoredRecommendations();
    if (stored?.recommendations) {
      const updatedRecommendations = stored.recommendations.map((rec) =>
        rec.id === id ? { ...rec, status: 'dismissed' } : rec
      );

      saveStoredRecommendations({
        ...stored,
        recommendations: updatedRecommendations,
      });

      // Update local state
      const activeRecommendations = updatedRecommendations.filter(
        (rec) => rec.status !== 'dismissed'
      );
      setRecommendations(activeRecommendations);
    } else {
      // If no stored data, just filter from current state
      setRecommendations((prev) => prev.filter((rec) => rec.id !== id));
    }
  };

  return { recommendations, dismissRecommendation, loading };
};

export default useRecommendations;
